import React, { useEffect, useState, useRef } from 'react';
import { GraduationCapIcon, MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { areaStudyOptions, programOptions, degreeLevelOptions } from '../data/filterOptions';
interface HeaderProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onNavigateToProgram?: (areaId: number, programId: number) => void;
  onNavigateToDegree?: (degreeLevel: number) => void;
}
export function Header({
  currentPage,
  onNavigate,
  onNavigateToProgram,
  onNavigateToDegree
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const [showDegreeDropdown, setShowDegreeDropdown] = useState(false);
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const degreeDropdownRef = useRef<HTMLDivElement>(null);
  const handleNav = (page: string) => {
    onNavigate(page);
    setMenuOpen(false);
  };
  const handleProgramClick = (areaId: number, programId: number) => {
    if (onNavigateToProgram) {
      onNavigate('compare');
      onNavigateToProgram(areaId, programId);
      setShowAreaDropdown(false);
      setSelectedArea(null);
    }
  };
  const handleDegreeClick = (degreeLevel: number) => {
    if (onNavigateToDegree) {
      onNavigateToDegree(degreeLevel);
      setShowDegreeDropdown(false);
      setMenuOpen(false);
    }
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAreaDropdown(false);
        setSelectedArea(null);
      }
      if (degreeDropdownRef.current && !degreeDropdownRef.current.contains(event.target as Node)) {
        setShowDegreeDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <header className="w-full bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-197">
      {/* Top Disclosure Banner - hidden on mobile */}
      <div className="hidden md:block w-full bg-gray-50 py-1.5 px-4 text-center border-b border-gray-200 relative" data-id="element-198">
        <p className="text-xs text-gray-500" data-id="element-199">
          We earn commissions from brands listed on this site, which influences
          how listings are presented.
          <button onMouseEnter={() => setShowDisclosure(true)} onMouseLeave={() => setShowDisclosure(false)} className="underline ml-1 hover:text-gray-700 relative" data-id="element-200">
            Advertising Disclosure
          </button>
        </p>

        {/* Disclosure Popup */}
        {showDisclosure && <div onMouseEnter={() => setShowDisclosure(true)} onMouseLeave={() => setShowDisclosure(false)} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-full max-w-2xl bg-white border border-gray-200 rounded-lg shadow-xl p-6 z-50 text-left" data-id="element-201">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-202">
              Advertising Disclosure
            </h3>
            <div className="text-xs text-gray-700 leading-relaxed space-y-2" data-id="element-203">
              <p data-id="element-204">
                www.compareonlinecolleges.com is a free online resource that
                strives to offer helpful content and comparison features to our
                visitors. We accept advertising compensation from companies that
                appear on the site, which impacts the location and order in
                which brands (and/or their products) are presented, and also
                impacts the score that is assigned to it.
              </p>
              <p data-id="element-205">
                Company listings on this page DO NOT imply endorsement. We do
                not feature all providers on the market. Except as expressly set
                forth in our Terms of Use, all representations and warranties
                regarding the information presented on this page are disclaimed.
              </p>
              <p data-id="element-206">
                The information, including pricing, which appears on this site
                is subject to change at any time.
              </p>
            </div>
          </div>}
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between" data-id="element-207">
        {/* Mobile: Hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-1.5 -ml-1.5 text-gray-600 hover:text-gray-900 transition-colors" aria-label="Toggle menu" data-id="element-208">
          {menuOpen ? <XIcon className="w-5 h-5" data-id="element-209" /> : <MenuIcon className="w-5 h-5" data-id="element-210" />}
        </button>

        {/* Logo - centered on mobile */}
        <button onClick={() => handleNav('compare')} className="flex items-center gap-2 hover:opacity-80 transition-opacity md:mr-auto" data-id="element-211">
          <div className="bg-[#e8594f] text-white p-1.5 rounded-lg" data-id="element-212">
            <GraduationCapIcon className="w-5 h-5" data-id="element-213" />
          </div>
          <span className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-214">
            FindOnlineDegrees
          </span>
        </button>

        {/* Mobile: spacer to balance hamburger for centering */}
        <div className="w-8 md:hidden" data-id="element-215"></div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" data-id="element-216">
          {/* Area of Study Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-217">
            <button onClick={() => setShowAreaDropdown(!showAreaDropdown)} className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-1" data-id="element-218">
              Area of Study
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAreaDropdown ? 'rotate-180' : ''}`} data-id="element-219" />
            </button>

            {showAreaDropdown && <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50" data-id="element-220">
                <div className="p-2" data-id="element-221">
                  {areaStudyOptions.slice(0, 8).map(area => {
                const isOpen = selectedArea === area.value;
                const programs = programOptions[area.value] || [];
                return <div key={area.value} className="mb-1" data-id="element-222">
                        <button onClick={() => setSelectedArea(isOpen ? null : area.value)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md transition-colors text-left" data-id="element-223">
                          <span data-id="element-224">{area.label}</span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-225" />
                        </button>

                        {isOpen && programs.length > 0 && <div className="ml-3 mt-1 space-y-1" data-id="element-226">
                            {programs.slice(0, 8).map(program => <button key={program.value} onClick={() => handleProgramClick(area.value, program.value)} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-[#3b82f6] hover:bg-blue-50 rounded-md transition-colors" data-id="element-227">
                                {program.label}
                              </button>)}
                          </div>}
                      </div>;
              })}
                </div>
              </div>}
          </div>

          {/* Degree Options Dropdown */}
          <div className="relative" ref={degreeDropdownRef} data-id="element-228">
            <button onClick={() => setShowDegreeDropdown(!showDegreeDropdown)} className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-1" data-id="element-229">
              Degree Options
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showDegreeDropdown ? 'rotate-180' : ''}`} data-id="element-230" />
            </button>

            {showDegreeDropdown && <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50" data-id="element-231">
                <div className="p-2" data-id="element-232">
                  {degreeLevelOptions.filter(d => d.value !== 5).map(degree => <button key={degree.value} onClick={() => handleDegreeClick(degree.value)} className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md transition-colors" data-id="element-233">
                        {degree.label}
                      </button>)}
                </div>
              </div>}
          </div>
        </nav>
      </div>

      {/* Mobile slide-down menu */}
      {menuOpen && <div className="md:hidden border-t border-gray-100 bg-white shadow-lg" data-id="element-234">
          <nav className="px-4 py-2" data-id="element-235">
            <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-236">
              Degree Options
            </p>
            {degreeLevelOptions.filter(d => d.value !== 5).map(degree => <button key={degree.value} onClick={() => handleDegreeClick(degree.value)} className="w-full text-left px-3 py-3 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-50" data-id="element-237">
                  {degree.label}
                </button>)}
          </nav>
        </div>}
    </header>;
}